import React from "react";
import {
    Box, Heading, Text, VStack, HStack, Badge, Icon, Flex, Button, Spinner, Alert
} from "@chakra-ui/react";
import { CheckCircleIcon, ViewIcon, DownloadIcon } from "@chakra-ui/icons";
import { FiFileText } from "react-icons/fi";
import { FaFileAlt } from "react-icons/fa";
import { getDisplayDocumentName } from "../utils/formatHelpers";

export const DocumentsTab = ({
    certifications, documents, downloadingDoc, downloadedDocs,
    downloadingAll, viewDocument, downloadDocumentAsPdf, downloadAllDocumentsForCert
}) => {
    const allDocs = Object.values(documents).flat();
    const totalDocs = allDocs.length;
    const doneDocs = allDocs.filter(d => d.status === 'done').length;

    return (
        <VStack spacing={6} align="stretch">
            {totalDocs > 0 && doneDocs < totalDocs && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                    <Spinner size="xs" mr={2} />
                    <Text>{doneDocs} of {totalDocs} documents ready</Text>
                </Alert>
            )}

            {certifications.map((cert) => {
                const certDocs = documents[cert.id] || [];
                if (certDocs.length === 0) return null;
                const doneDocCount = certDocs.filter(d => d.status === 'done').length;
                const undownloadedPdfs = certDocs.filter(d => d.status === 'done' && d.isPdf && !downloadedDocs.has(d.id || d.title));

                return (
                    <Box key={cert.id} p={{ base: 3, md: 5 }} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
                        <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb={4} flexDirection={{ base: "column", md: "row" }} gap={3}>
                            <HStack spacing={2} flex="1" minW={0} alignItems="flex-start">
                                <Icon as={FiFileText} color="brand.600" boxSize={{ base: 4, md: 5 }} flexShrink={0} mt={0.5} />
                                <Heading size={{ base: "xs", md: "sm" }} color="brand.700" flex="1" minW={0}>
                                    {cert.full_certification?.name}
                                </Heading>
                                {certDocs.length > 0 && doneDocCount < certDocs.length && (
                                    <Alert status="info" size="sm" borderRadius="md" px={2} py={1} w="auto" bg="blue.50" flexShrink={0}>
                                        <Spinner size="xs" mr={1} />
                                        <Text fontSize="2xs">{doneDocCount}/{certDocs.length} ready</Text>
                                    </Alert>
                                )}
                            </HStack>
                            {undownloadedPdfs.length > 0 && (
                                <Button
                                    leftIcon={<DownloadIcon />} colorScheme="teal"
                                    size={{ base: "xs", md: "sm" }}
                                    onClick={() => downloadAllDocumentsForCert(cert.id)}
                                    isLoading={downloadingAll[cert.id]}
                                    loadingText="Downloading..."
                                    w={{ base: "100%", md: "auto" }}
                                    flexShrink={0}
                                    whiteSpace="nowrap"
                                >
                                    <Text display={{ base: "none", sm: "block" }}>Download All PDFs ({undownloadedPdfs.length})</Text>
                                    <Text display={{ base: "block", sm: "none" }}>Download All ({undownloadedPdfs.length})</Text>
                                </Button>
                            )}
                        </Flex>

                        <VStack spacing={2} align="stretch">
                            {certDocs.map((doc, index) => {
                                const isDone = doc.status === 'done';
                                const isGenerating = doc.status === 'generating' || doc.status === 'not_started';
                                const isDownloaded = downloadedDocs.has(doc.id || doc.title);

                                return (
                                    <Flex
                                        key={doc.id || index}
                                        align={{ base: "flex-start", md: "center" }}
                                        p={{ base: 3, md: 3 }}
                                        border="1px solid"
                                        borderColor={isDone ? "green.200" : isGenerating ? "yellow.200" : "gray.200"}
                                        borderRadius="md"
                                        bg={isDone ? "white" : isGenerating ? "yellow.50" : "gray.50"}
                                        justify="space-between"
                                        _hover={isDone ? { bg: "gray.50", borderColor: "brand.400" } : {}}
                                        transition="all 0.2s"
                                        flexDirection={{ base: "column", md: "row" }}
                                        gap={{ base: 2, md: 0 }}
                                    >
                                        <HStack spacing={3} flex="1" mr={{ base: 0, md: 4 }} w={{ base: "100%", md: "auto" }}>
                                            {isDone ? (
                                                <Box color="green.500" fontSize={{ base: "18px", md: "20px" }} flexShrink={0}><CheckCircleIcon /></Box>
                                            ) : isGenerating ? (
                                                <Spinner size="sm" color="yellow.500" flexShrink={0} />
                                            ) : (
                                                <Box color="gray.400" fontSize={{ base: "18px", md: "20px" }} flexShrink={0}><FaFileAlt /></Box>
                                            )}
                                            <VStack align="start" spacing={0} flex={1} minW={0}>
                                                <HStack spacing={2} flexWrap="wrap">
                                                    <Text fontWeight="semibold" fontSize={{ base: "xs", md: "sm" }} color={isDone ? "gray.900" : "gray.600"} noOfLines={1}>
                                                        {getDisplayDocumentName(doc.title)}
                                                    </Text>
                                                    {isDone && <Badge colorScheme="green" fontSize="2xs">Done</Badge>}
                                                    {isGenerating && <Badge colorScheme="yellow" fontSize="2xs">Generating</Badge>}
                                                </HStack>
                                                <Text fontSize="2xs" color="gray.500">{isDone ? 'PDF ready' : 'Generating...'}</Text>
                                            </VStack>
                                        </HStack>
                                        <HStack spacing={2} w={{ base: "100%", md: "auto" }} justify={{ base: "flex-end", md: "auto" }}>
                                            <Button size="xs" variant="outline" leftIcon={<ViewIcon />} onClick={() => viewDocument(doc)} isDisabled={!isDone} flex={{ base: 1, md: "none" }}>
                                                View
                                            </Button>
                                            <Button
                                                size="xs"
                                                colorScheme={isDownloaded ? "green" : "blue"}
                                                leftIcon={isDownloaded ? <CheckCircleIcon /> : <DownloadIcon />}
                                                onClick={() => downloadDocumentAsPdf(doc)}
                                                isLoading={downloadingDoc === (doc.id || doc.title)}
                                                loadingText="..."
                                                isDisabled={!isDone}
                                                flex={{ base: 1, md: "none" }}
                                            >
                                                <Text display={{ base: "none", sm: "block" }}>{isDownloaded ? "Downloaded" : "Download"}</Text>
                                                <Text display={{ base: "block", sm: "none" }}>{isDownloaded ? "Done" : "PDF"}</Text>
                                            </Button>
                                        </HStack>
                                    </Flex>
                                );
                            })}
                        </VStack>
                    </Box>
                );
            })}

            {totalDocs === 0 && (
                <Box p={6} bg="white" borderRadius="md" textAlign="center">
                    <Text color="gray.500" fontSize="sm">No documents available at this time.</Text>
                </Box>
            )}
        </VStack>
    );
};
